// @flow

import styled from '@emotion/styled'
import * as React from 'react'

import { animations, colors, media } from '@toggl/style'
import typeof { Global, Hire, Plan, Track } from '@toggl/ui'

import * as Button from '../button'
import * as text from '../text'

type Props = {
  Cta: React.ComponentType<any>,
  discoverMore: React.Node,
  image?: string,
  logo: React.Element<Global | Plan | Track | Hire>,
  title: string,
  discoverMore: React.Node,
  shoutOut?: {
    text: string,
    url: string,
    active: boolean,
  },
  video?: React.Node,
}

export default function Product({
  Cta,
  logo,
  image,
  title,
  video,
  discoverMore,
  shoutOut,
}: Props) {
  return (
    <Root>
      <LogoWrapper>{logo}</LogoWrapper>
      <ShoutOutWrapper>
        {shoutOut?.active && (
          <ShoutOut href={shoutOut.url}>
            <span>{shoutOut.text}</span>
            <span>Learn more</span>
          </ShoutOut>
        )}
        {image ? <Image src={image} /> : <VideoWrapper>{video}</VideoWrapper>}
      </ShoutOutWrapper>

      <Title color={colors.darkPurple}>{title}</Title>
      <CtaWrapper>
        <Cta />
      </CtaWrapper>
      <ButtonReadMore caretDirection="right">{discoverMore}</ButtonReadMore>
    </Root>
  )
}

const ShoutOut = styled.a`
  border-radius: 50%;
  width: 100px;
  height: 100px;
  background: ${colors.yellow};
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${text.offerText}
  line-height: 1;
  color: ${colors.darkPurple};
  position: absolute;
  z-index: 10;
  left: -50px;
  top: 10px;
  padding: 10px;
  span {
    &:first-of-type {
      display: block;
      margin: 5px 0 2px;
    }
    &:nth-of-type(2) {
      ${text.paragraph3};
      font-size: 11px;
    }
  }
  &:hover {
    background: ${colors.fadedPurpleAlt};
    color: ${colors.yellow};
  }
`

const ShoutOutWrapper = styled.div`
  position: relative;
`

const VideoWrapper = styled.div`
  width: 160px;
  height: 160px;
  border-radius: 50%;
  overflow: hidden;
  background-color: ${(p) => p.theme.primary};

  video {
    max-width: 100%;
  }
`

const Image = styled.img`
  height: 123px;
  ${media.mq[1]} {
    height: 150px;
  }
`

const ButtonReadMore = styled(Button.Secondary)`
  margin-top: 17px;
  color: ${(p) => p.theme.primary};

  span {
    border-left-color: ${(p) => p.theme.primary};
  }

  ${media.mq[1]} {
    margin-top: 23px;
    color: ${(p) => p.theme.primary};
  }

  a {
    color: ${(p) => p.theme.primary};
    &:hover {
      color: ${colors.fadedPurple};
      + span {
        border-left-color: ${colors.fadedPurple};
      }
    }
  }
`

const Title = styled(text.H4)`
  margin: 25px 0;
  ${media.mq[1]} {
    min-height: 60px;
  }
`

const LogoWrapper = styled.div`
  height: 28px;
  margin-bottom: 24px;

  svg {
    height: 100%;
  }

  ${media.mq[1]} {
    height: 32px;
    margin-bottom: 32px;
  }
`

const CtaWrapper = styled.div``

const Root: React.ComponentType<any> = styled.div`
  ${text.defaultText};
  background-color: ${colors.beige};
  animation: 700ms ease-in-out ${animations.cardUp};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-height: 410px;
  padding: 24px 27px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  ${media.mq[1]} {
    flex: 1;
    max-width: 500px;
    margin: 10px;
    min-height: 500px;
    padding: 40px 48px;
  }
`

export const ProductSection: React.ComponentType<any> = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  ${media.mq[1]} {
    gap: 40px;
    flex-direction: row;
  }
`
